import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';
import UserService from "../../Services/UserService";
import ProjectsService from '../../Services/ProjectsService';
import InfoDeleteProfile from "./InfoDeleteProfile";
import OtpinputModal from "../Company/OtpInputModal";

// Constants for service request navigation
const SERVICE_REQUEST_CONFIG = {
  requestType: 'ManageCompany',
  requestSubType: 'ReplaceCRep',
  usrExist: 'false'
};

function DeleteAccount({ userEmail }) {
  const navigate = useNavigate();
  
  // State management
  const [infoModelOpen, setInfoModelOpen] = useState(false);
  const [infoConfirmModelOpen, setInfoConfirmModelOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpDataFromModal, setOtpDataFromModal] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [repId, setRepId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Logout utility function
  const logoutUser = () => {
    const cookies = new Cookies();
    cookies.remove('accessToken');
    cookies.remove('userId');
    cookies.remove('currentAPIMUser');
    sessionStorage.clear();
  };

  // Fetch company representative details
  const openDeletePopup = async (id) => {
    try {
      setInfoModelOpen(true);
      setCompanyId(id);
      
      const response = await ProjectsService.getCompaniesCrepDetails(id);
      const filteredRepId = response.data.representatives
        .filter(rep => rep.companyId === id && rep.email === userEmail)
        .map(item => item.id);
      
      setRepId(filteredRepId);
    } catch (error) {
      console.error('Error fetching company rep details:', error);
      toast.error('Failed to fetch company details. Please try again.');
    }
  };

  // Check if user can delete account
  const handleDelete = async () => {
    try {
      setIsLoading(true);
      const response = await ProjectsService.getAllProjects();
      const delegatableProjects = response.data[0].companyProjects
        .filter(proj => proj.userCanAddDelegate && proj.userCanRemoveDelegate);

      if (delegatableProjects.length > 0) {
        await openDeletePopup(delegatableProjects[0].id);
      } else {
        setInfoConfirmModelOpen(true);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to fetch projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle modal action
  const infoOkButtonAction = (actionName) => {
    if (actionName === 'DELETE') {
      deleteAction();
    } else if (actionName === 'SRQ') {
      navigate(`/serviceRequest/${companyId}/${repId}/${SERVICE_REQUEST_CONFIG.requestType}/${SERVICE_REQUEST_CONFIG.requestSubType}/${SERVICE_REQUEST_CONFIG.usrExist}`);
    }
  };

  // Request strong confirmation code
  const deleteAction = async () => {
    try {
      setIsLoading(true);
      const authURL = '/users/current/';
      const response = await UserService.getStrongConfirmationCode(authURL, "DELETE");
      const code = response.data.code;

      if (response.data.channel === 'SMS') {
        setOtpModalOpen(true);
      } else {
        await deleteUser(code);
      }
    } catch (error) {
      console.error('Error requesting confirmation code:', error);
      toast.error('Failed to request confirmation code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const verifyOtp = async (otpData) => {
    if (otpData.length > 0) {
      const otpCode = Number(otpData.join(''));
      await deleteUser(otpCode);
    }
  };

  // Delete user account
  const deleteUser = async (xCode) => {
    try {
      setIsLoading(true);
      const response = await UserService.deleteUserAccount(xCode);
      toast.success(response.data.message || 'Account deleted successfully');
      
      // Close modal and logout
      setOtpModalOpen(false);
      setTimeout(() => {
        logoutUser();
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.message || 'Failed to delete account. Please try again.');
      setOtpModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const resendOtp = async () => {
    try {
      const authURL = '/users/current/';
      await UserService.getStrongConfirmationCode(authURL, "DELETE");
      toast.info('OTP resent successfully');
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error('Failed to resend OTP. Please try again.');
    }
  };

  return (
    <>
      <ul className="list-unstyled">
        <li>
          <h3>Delete My Account</h3>
        </li>
        <li className="mt-3">
          <p>
            According to our{" "}
            <a href="/Privacy-Notice-API-Market-Place.pdf" target="_blank" rel="noreferrer">
              privacy policy
            </a>
            , we may keep your data for up to 6 months.
          </p>
        </li>
        <li>
          <div className="d-flex align-items-center justify-content-end">
            <button
              type="button"
              className="comm-button-style delete mt-3"
              onClick={handleDelete}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Delete my account'}
            </button>
          </div>
        </li>
      </ul>

      {infoModelOpen && (
        <InfoDeleteProfile
          modalHeading="Delete My Account"
          setInfoModelOpen={setInfoModelOpen}
          displayMessage="You are the confirmed representative of your companies and hence you cannot delete your account.\n\nYou must raise a service request so we can identify your replacement(s) before deleting your account"
          action="SRQ"
          infoOkButtonAction={infoOkButtonAction}
        />
      )}

      {infoConfirmModelOpen && (
        <InfoDeleteProfile
          modalHeading="Delete My Account"
          setInfoModelOpen={setInfoConfirmModelOpen}
          displayMessage="Please confirm that you wish to delete your account"
          action="DELETE"
          infoOkButtonAction={infoOkButtonAction}
        />
      )}

      {otpModalOpen && (
        <OtpinputModal
          ModalHeading="Verify OTP"
          setOpenOtpModal={setOtpModalOpen}
          handleOtp={setOtpDataFromModal}
          value={otpDataFromModal}
          verifyOtp={verifyOtp}
          credentialRecord=""
          otpInputModelOpenendForAction=""
          newMobile=""
          resendOtp={resendOtp}
        />
      )}
    </>
  );
}

export default DeleteAccount;